// src/pages/LMS/LMSEmbedViewer.js
/**
 * Public Embed Viewer Component
 * Renders the course catalog based on embed page configuration
 * This is what gets displayed when someone embeds the iframe on their website
 * 
 * Supports three layout types:
 * - grid: Cards in a responsive grid
 * - list: Horizontal cards in a vertical list
 * - carousel: Horizontal scrollable carousel
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardContent, CardMedia, CardActions,
  Chip, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Pagination, CircularProgress, Stack, IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  School as SchoolIcon,
  AccessTime as DurationIcon,
  MenuBook as ModulesIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as DiscountIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';

const LMSEmbedViewer = () => {
  const { token } = useParams();
  const carouselRef = useRef(null);
  
  // State
  const [config, setConfig] = useState(null);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Load config and courses
  useEffect(() => {
    loadConfig();
  }, [token]);

  useEffect(() => {
    if (config) {
      loadCourses();
    }
  }, [config, page, searchTerm, categoryFilter, difficultyFilter]);

  const loadConfig = async () => {
    try {
      const response = await axiosInstance.get(`/api/lms/embed/public/${token}/config`);
      setConfig(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load embed configuration');
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category = categoryFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;

      const response = await axiosInstance.get(`/api/lms/embed/public/${token}/courses`, { params });
      setCourses(response.data.data.courses || []);
      setCategories(response.data.data.categories || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (err) {
      console.error('Error loading courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const trackEvent = async (eventType, courseId = null, courseIdentity = null) => {
    try {
      await axiosInstance.post(`/api/lms/embed/public/${token}/track`, {
        event_type: eventType,
        course_id: courseId,
        course_identity: courseIdentity
      });
    } catch (err) {
      // Silent fail for analytics
    }
  };

  const handleEnrollClick = (course) => {
    trackEvent('enroll_click', course.id, course.course_identity);
    
    let redirectUrl = config?.redirect?.enroll_url || config?.redirect?.enroll_template || '';
    
    if (redirectUrl) {
      redirectUrl = redirectUrl
        .replace('{course_id}', course.id)
        .replace('{course_identity}', course.course_identity)
        .replace('{course_title}', encodeURIComponent(course.title));
      
      if (config?.redirect?.open_new_tab) {
        window.open(redirectUrl, '_blank');
      } else {
        window.location.href = redirectUrl;
      }
    }
  };

  // Carousel scroll handlers
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320; // Card width + gap
      const newScrollLeft = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  // Generate styles from config
  const styles = useMemo(() => {
    if (!config) return {};
    
    return {
      container: {
        backgroundColor: config.colors?.background || '#f5f5f5',
        minHeight: config.dimensions?.min_height || '400px',
        padding: config.dimensions?.padding || '24px',
        borderRadius: config.dimensions?.border_radius || '12px',
        fontFamily: config.typography?.font_family || 'inherit',
        width: config.dimensions?.width || '100%',
        maxWidth: config.dimensions?.max_width || '1200px',
        margin: '0 auto',
      },
      header: {
        backgroundColor: config.colors?.header_background || '#ffffff',
        padding: '32px 24px',
        borderRadius: config.dimensions?.border_radius || '12px',
        marginBottom: '24px',
        textAlign: config.header?.alignment || 'center',
      },
      headerTitle: {
        color: config.colors?.heading || '#1a1a1a',
        fontSize: config.typography?.header_title_size || '28px',
        fontWeight: config.typography?.heading_font_weight || '700',
      },
      headerSubtitle: {
        color: config.colors?.subtext || '#666666',
        fontSize: config.typography?.header_subtitle_size || '16px',
      },
      card: {
        backgroundColor: config.colors?.card_background || '#ffffff',
        borderRadius: config.dimensions?.card_border_radius || '8px',
        border: `1px solid ${config.colors?.card_border || '#e0e0e0'}`,
        boxShadow: config.colors?.card_shadow || '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-4px)',
        },
      },
      // List layout specific card style
      listCard: {
        backgroundColor: config.colors?.card_background || '#ffffff',
        borderRadius: config.dimensions?.card_border_radius || '8px',
        border: `1px solid ${config.colors?.card_border || '#e0e0e0'}`,
        boxShadow: config.colors?.card_shadow || '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'row',
        marginBottom: config.layout?.gap || '16px',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
      },
      // Carousel layout specific card style
      carouselCard: {
        backgroundColor: config.colors?.card_background || '#ffffff',
        borderRadius: config.dimensions?.card_border_radius || '8px',
        border: `1px solid ${config.colors?.card_border || '#e0e0e0'}`,
        boxShadow: config.colors?.card_shadow || '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        minWidth: '300px',
        maxWidth: '300px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-4px)',
        },
      },
      cardTitle: {
        color: config.colors?.heading || '#1a1a1a',
        fontSize: config.typography?.card_title_size || '18px',
        fontWeight: config.typography?.card_title_font_weight || '600',
      },
      cardDescription: {
        color: config.colors?.subtext || '#666666',
        fontSize: config.typography?.card_description_size || '14px',
      },
      difficultyBeginner: {
        backgroundColor: config.colors?.difficulty_beginner || '#4caf50',
        color: '#ffffff',
      },
      difficultyIntermediate: {
        backgroundColor: config.colors?.difficulty_intermediate || '#ff9800',
        color: '#ffffff',
      },
      difficultyAdvanced: {
        backgroundColor: config.colors?.difficulty_advanced || '#f44336',
        color: '#ffffff',
      },
      categoryChip: {
        backgroundColor: config.colors?.category_chip || '#e3f2fd',
        color: config.colors?.category_chip_text || '#1976d2',
      },
      priceBadge: {
        backgroundColor: config.colors?.price_badge || '#1976d2',
        color: config.colors?.price_badge_text || '#ffffff',
      },
      discountBadge: {
        backgroundColor: config.colors?.discount_badge || '#dc004e',
        color: config.colors?.discount_badge_text || '#ffffff',
      },
      freeBadge: {
        backgroundColor: config.colors?.free_badge || '#4caf50',
        color: config.colors?.free_badge_text || '#ffffff',
      },
    };
  }, [config]);

  const getDifficultyStyle = (level) => {
    switch (level) {
      case 'beginner':
        return styles.difficultyBeginner;
      case 'intermediate':
        return styles.difficultyIntermediate;
      case 'advanced':
        return styles.difficultyAdvanced;
      default:
        return {};
    }
  };

  // Render course card content (shared between layouts)
  const renderCardContent = (course, isListLayout = false) => (
    <>
      <CardContent sx={{ flexGrow: 1, p: isListLayout ? 2 : undefined }}>
        {/* Badges */}
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {config.card_display?.show_difficulty && course.difficulty_level && (
            <Chip
              label={course.difficulty_level.charAt(0).toUpperCase() + course.difficulty_level.slice(1)}
              size="small"
              sx={getDifficultyStyle(course.difficulty_level)}
            />
          )}
          {config.card_display?.show_category && course.category && (
            <Chip
              label={course.category}
              size="small"
              sx={styles.categoryChip}
            />
          )}
          {/* Price Badge */}
          {config.card_display?.show_price !== false && (
            course.is_paid ? (
              course.is_discount_active && config.card_display?.show_discount_badge !== false ? (
                <Chip
                  icon={<DiscountIcon sx={{ fontSize: 14 }} />}
                  label={`$${course.effective_price || course.price} (was $${course.price})`}
                  size="small"
                  sx={styles.discountBadge}
                />
              ) : (
                <Chip
                  icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                  label={`$${course.price || 0}`}
                  size="small"
                  sx={styles.priceBadge}
                />
              )
            ) : (
              <Chip
                label="Free"
                size="small"
                sx={styles.freeBadge}
              />
            )
          )}
        </Stack>

        {/* Title */}
        {config.card_display?.show_title !== false && (
          <Typography 
            variant="h6" 
            sx={{ ...styles.cardTitle, mb: 1 }}
            title={course.title}
          >
            {course.title}
          </Typography>
        )}

        {/* Description */}
        {config.card_display?.show_description && course.short_description && (
          <Typography 
            sx={{
              ...styles.cardDescription,
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: config.card_display?.description_lines || 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {course.short_description}
          </Typography>
        )}

        {/* Meta Info */}
        <Stack direction="row" spacing={2} sx={{ mt: 'auto' }}>
          {config.card_display?.show_duration && course.estimated_duration > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DurationIcon sx={{ fontSize: 16, color: config.colors?.subtext }} />
              <Typography variant="caption" sx={{ color: config.colors?.subtext }}>
                {course.estimated_duration}h
              </Typography>
            </Box>
          )}
          {config.card_display?.show_module_count && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ModulesIcon sx={{ fontSize: 16, color: config.colors?.subtext }} />
              <Typography variant="caption" sx={{ color: config.colors?.subtext }}>
                {course.module_count || 0} modules
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Actions */}
      {config.buttons?.show_enroll && (
        <CardActions sx={{ p: 2, pt: 0, ...(isListLayout && { alignSelf: 'center', pr: 3 }) }}>
          <Button
            variant={config.buttons?.enroll_style === 'outlined' ? 'outlined' : 'contained'}
            fullWidth={!isListLayout && config.buttons?.enroll_full_width}
            onClick={() => handleEnrollClick(course)}
            sx={{
              backgroundColor: config.buttons?.enroll_style === 'filled' 
                ? config.colors?.button 
                : 'transparent',
              color: config.buttons?.enroll_style === 'filled'
                ? config.colors?.button_text
                : config.colors?.button,
              borderColor: config.colors?.button,
              borderRadius: config.buttons?.button_border_radius,
              '&:hover': {
                backgroundColor: config.buttons?.enroll_style === 'filled'
                  ? config.colors?.button_hover
                  : 'rgba(25, 118, 210, 0.1)',
                borderColor: config.colors?.button_hover,
              },
            }}
          >
            {config.buttons?.enroll_text || 'Enroll Now'}
          </Button>
        </CardActions>
      )}
    </>
  );

  // Render Grid Layout
  const renderGridLayout = () => {
    const columns = config.layout?.columns || 3;
    const gridSize = 12 / columns;
    const gap = config.layout?.gap ? parseInt(config.layout.gap) / 8 : 3;

    return (
      <Grid container spacing={gap}>
        {courses.map((course) => (
          <Grid item xs={12} sm={6} md={gridSize} key={course.id}>
            <Card sx={styles.card} className="embed-course-card">
              {/* Thumbnail */}
              {config.card_display?.show_thumbnail && (
                <CardMedia
                  component="div"
                  sx={{
                    height: config.card_display?.thumbnail_height || '180px',
                    backgroundColor: config.colors?.card_border,
                    backgroundImage: `url(${course.thumbnail_url || config.card_display?.default_thumbnail_url || ''})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!course.thumbnail_url && !config.card_display?.default_thumbnail_url && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: config.colors?.primary,
                      opacity: 0.1
                    }}>
                      <SchoolIcon sx={{ fontSize: 64, color: config.colors?.primary }} />
                    </Box>
                  )}
                </CardMedia>
              )}
              {renderCardContent(course)}
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render List Layout
  const renderListLayout = () => {
    return (
      <Box>
        {courses.map((course) => (
          <Card key={course.id} sx={styles.listCard} className="embed-course-card">
            {/* Thumbnail on left */}
            {config.card_display?.show_thumbnail && (
              <CardMedia
                component="div"
                sx={{
                  width: '200px',
                  minWidth: '200px',
                  backgroundColor: config.colors?.card_border,
                  backgroundImage: `url(${course.thumbnail_url || config.card_display?.default_thumbnail_url || ''})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!course.thumbnail_url && !config.card_display?.default_thumbnail_url && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    minHeight: '150px',
                    backgroundColor: config.colors?.primary,
                    opacity: 0.1
                  }}>
                    <SchoolIcon sx={{ fontSize: 48, color: config.colors?.primary }} />
                  </Box>
                )}
              </CardMedia>
            )}
            {/* Content on right */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {renderCardContent(course, true)}
            </Box>
          </Card>
        ))}
      </Box>
    );
  };

  // Render Carousel Layout
  const renderCarouselLayout = () => {
    const gap = config.layout?.gap ? parseInt(config.layout.gap) : 24;

    return (
      <Box sx={{ position: 'relative' }}>
        {/* Left Arrow */}
        <IconButton
          onClick={() => scrollCarousel('left')}
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: config.colors?.card_background || '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: config.colors?.card_background || '#fff',
            },
          }}
        >
          <ChevronLeftIcon sx={{ color: config.colors?.text }} />
        </IconButton>

        {/* Carousel Container */}
        <Box
          ref={carouselRef}
          sx={{
            display: 'flex',
            gap: `${gap}px`,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            pb: 2,
            px: 1,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: config.colors?.background || '#f5f5f5',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: config.colors?.card_border || '#e0e0e0',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: config.colors?.subtext || '#999',
              },
            },
          }}
        >
          {courses.map((course) => (
            <Card key={course.id} sx={styles.carouselCard} className="embed-course-card">
              {/* Thumbnail */}
              {config.card_display?.show_thumbnail && (
                <CardMedia
                  component="div"
                  sx={{
                    height: config.card_display?.thumbnail_height || '160px',
                    backgroundColor: config.colors?.card_border,
                    backgroundImage: `url(${course.thumbnail_url || config.card_display?.default_thumbnail_url || ''})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!course.thumbnail_url && !config.card_display?.default_thumbnail_url && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: config.colors?.primary,
                      opacity: 0.1
                    }}>
                      <SchoolIcon sx={{ fontSize: 48, color: config.colors?.primary }} />
                    </Box>
                  )}
                </CardMedia>
              )}
              {renderCardContent(course)}
            </Card>
          ))}
        </Box>

        {/* Right Arrow */}
        <IconButton
          onClick={() => scrollCarousel('right')}
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: config.colors?.card_background || '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: config.colors?.card_background || '#fff',
            },
          }}
        >
          <ChevronRightIcon sx={{ color: config.colors?.text }} />
        </IconButton>
      </Box>
    );
  };

  // Render courses based on layout type
  const renderCourses = () => {
    if (courses.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <SchoolIcon sx={{ fontSize: 64, color: config.colors?.subtext, mb: 2 }} />
          <Typography variant="h6" sx={{ color: config.colors?.heading }}>
            {config.empty_state?.title || 'No courses available'}
          </Typography>
          <Typography sx={{ color: config.colors?.subtext }}>
            {config.empty_state?.message || 'Check back later for new courses.'}
          </Typography>
        </Box>
      );
    }

    const layoutType = config.layout?.type || 'grid';

    switch (layoutType) {
      case 'list':
        return renderListLayout();
      case 'carousel':
        return renderCarouselLayout();
      case 'grid':
      default:
        return renderGridLayout();
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Custom CSS */}
      {config.custom_code?.css && (
        <style>{config.custom_code.css}</style>
      )}

      {/* Header */}
      {config.header?.show && (
        <Box sx={styles.header} className="embed-header">
          {config.header?.show_logo && config.header?.logo_url && (
            <Box sx={{ mb: 2 }}>
              <img 
                src={config.header.logo_url} 
                alt="Logo" 
                style={{ height: config.header.logo_height || '40px' }}
              />
            </Box>
          )}
          <Typography variant="h4" sx={styles.headerTitle}>
            {config.header?.title || 'Our Courses'}
          </Typography>
          {config.header?.subtitle && (
            <Typography sx={{ ...styles.headerSubtitle, mt: 1 }}>
              {config.header.subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Filters */}
      {(config.filters?.show_search || config.filters?.show_category_filter || config.filters?.show_difficulty_filter) && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {config.filters?.show_search && (
            <TextField
              size="small"
              placeholder={config.filters?.search_placeholder || 'Search courses...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: config.colors?.subtext }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                flex: 1, 
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: config.colors?.card_background,
                }
              }}
            />
          )}
          
          {config.filters?.show_category_filter && categories.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                label="Category"
                sx={{ backgroundColor: config.colors?.card_background }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {config.filters?.show_difficulty_filter && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => {
                  setDifficultyFilter(e.target.value);
                  setPage(1);
                }}
                label="Difficulty"
                sx={{ backgroundColor: config.colors?.card_background }}
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      )}

      {/* Courses */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress sx={{ color: config.colors?.primary }} />
        </Box>
      ) : (
        renderCourses()
      )}

      {/* Pagination - Only show for grid and list layouts */}
      {config.pagination?.enabled && 
       totalPages > 1 && 
       config.layout?.type !== 'carousel' && 
       courses.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          {config.pagination?.style === 'load_more' ? (
            <Button
              variant="outlined"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              sx={{
                borderColor: config.colors?.button,
                color: config.colors?.button,
              }}
            >
              Load More
            </Button>
          ) : (
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: config.colors?.text,
                },
                '& .Mui-selected': {
                  backgroundColor: `${config.colors?.button} !important`,
                  color: `${config.colors?.button_text} !important`,
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Footer */}
      {config.footer?.show && (
        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${config.colors?.card_border}`, textAlign: 'center' }}>
          {config.footer?.text && (
            <Typography sx={{ color: config.colors?.subtext, mb: 1 }}>
              {config.footer.text}
            </Typography>
          )}
          {config.footer?.show_powered_by && (
            <Typography variant="caption" sx={{ color: config.colors?.subtext }}>
              Powered by LMS Platform
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LMSEmbedViewer;